import { forwardRef, useCallback, useEffect, useState, type InputHTMLAttributes } from "react"

import {
  Autocomplete,
  Box,
  ButtonBase,
  Checkbox,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  type BoxProps,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { noop, truncate } from "lodash"

import type { FC } from ".."
import { SvgIcon } from ".."

type Facet = {
  label: string
  type?: string
  experienceType?: string
  setFilters?: () => void
  filters?: Facet[]
}

type FacetProps = {
  count: number
  sortOptions?: Facet[]
  facets: Facet[]
  removeAllFilters?: () => void
  removeIndividualFilter?: (label: string) => void
  onSpecialsToggle?: (value: boolean) => void
  isSpecialsChecked?: boolean
  showSpecialsToggle?: boolean
  sortChange?: (value: string) => void
  paramSort?: string | null
}

const SearchFacets: FC<FacetProps> = ({
  count,
  facets = [],
  sortOptions = [],
  removeAllFilters,
  removeIndividualFilter,
  sortChange = noop,
  onSpecialsToggle = noop,
  isSpecialsChecked,
  showSpecialsToggle = true,
  paramSort,
}) => {
  const [filters, setFilters] = useState(facets)

  useEffect(() => {
    setFilters(facets)
  }, [facets])

  return (
    <Box
      display="flex"
      gap={{ xs: 5, md: 10 }}
      px={5}
      alignItems="center"
      justifyContent="space-between"
      minHeight={100}
      flexWrap={{ xs: "wrap", md: "nowrap" }}
    >
      <Stack
        direction="column"
        alignSelf={{ xs: "flex-start", md: "center" }}
        sx={{
          "&::after": {
            content: '""',
            height: 0,
            flexBasis: "100%",
          },
        }}
      >
        <Typography variant="body.sm" sx={{ py: { xs: 2, md: 0 }, pb: { xs: 0, md: 2 } }}>
          {count} Trips found
        </Typography>
        <Stack direction="row" alignItems="center" columnGap={2} rowGap={2} flexWrap="wrap">
          {facets.map((facet) => (
            <FacetItem
              key={facet.label}
              removeIndividualFilter={removeIndividualFilter}
              setFilters={setFilters as () => void}
              filters={filters}
              {...facet}
            />
          ))}
          {facets.length > 0 && <RemoveAllFacet onClick={removeAllFilters} />}
        </Stack>
      </Stack>
      <Box
        display="flex"
        alignItems="center"
        gap={5}
        alignSelf={{ xs: "flex-start", md: "center" }}
      >
        {showSpecialsToggle && (
          <SpecialsCheckbox checked={isSpecialsChecked as boolean} onChange={onSpecialsToggle} />
        )}
        {sortOptions?.length > 0 && (
          <Box display={{ xs: "none", md: "block" }}>
            <SortBySelect sortOptions={sortOptions} onChange={sortChange} paramSort={paramSort} />
          </Box>
        )}
      </Box>
    </Box>
  )
}
export default SearchFacets

const FacetItem: FC<Facet & Pick<FacetProps, "removeIndividualFilter">> = ({
  label,
  type,
  experienceType,
  removeIndividualFilter,
}) => {
  const truncatedLabel = truncate(label, { length: 20, omission: "..." })
  const removeFilter = useCallback(() => {
    const facet = { label, type, experienceType }
    if (removeIndividualFilter) removeIndividualFilter(facet as any)
  }, [])

  return (
    <ButtonBase
      onClick={removeFilter}
      sx={(theme) => ({
        columnGap: 4,
        height: 33,
        borderRadius: 9999,
        border: `1px solid ${theme.palette["neutral.200"].main}`,
        py: 3,
        px: 4,
      })}
    >
      <Typography variant="link.xs" sx={{ whiteSpace: "nowrap" }}>
        {truncatedLabel}
      </Typography>
      <SvgIcon variant="cross" size={6} />
    </ButtonBase>
  )
}

type SortBySelectProps = {
  sortOptions?: Facet[]
  onChange?: (value: string) => void
  paramSort?: string | null
}

const SortBySelect: FC<SortBySelectProps> = ({
  sortOptions = [],
  onChange = noop,
  paramSort = null,
}) => {
  const defaultSort = sortOptions.find((option) => option.type === paramSort)
  return (
    <Autocomplete
      disablePortal
      openOnFocus
      blurOnSelect
      options={sortOptions || []}
      defaultValue={defaultSort || null}
      getOptionLabel={(option) => {
        return option?.label || "Unknown"
      }}
      onChange={(event, option) => {
        onChange(option?.type || "")
      }}
      renderInput={(params) => (
        <SortByField
          ref={params.InputProps.ref}
          placeholder="Sort By"
          aria-label="Sort By"
          inputProps={params.inputProps}
        />
      )}
      renderOption={(props, option, { selected }) => (
        <ListItem
          disablePadding
          selected={selected}
          sx={(theme) => ({
            "border": `1px solid transparent`,
            "minHeight": "48px!important",
            "py": 3.5,
            "px": 4,

            "&.Mui-focused, &.Mui-selected": {
              bgcolor: `${theme.palette["secondary.200"].main}!important`,
              borderRadius: 1,
              color: `${theme.palette["secondary.800"].main}!important`,
            },
            "&.Mui-selected": {
              borderColor: theme.palette["secondary.300"].main,
            },
          })}
          {...props}
        >
          <ListItemText primaryTypographyProps={{ variant: "link.xs" }}>
            {option?.label || "Unknown"}
          </ListItemText>
        </ListItem>
      )}
      ListboxProps={{
        style: {
          maxHeight: 324,
        },
        sx: {
          p: 2,
        },
      }}
      slotProps={{
        popper: {
          placement: "bottom-end",
          sx: {
            minWidth: { sm: "276px!important" },
          },
        },
        paper: {
          sx: {
            borderRadius: 1,
            bgcolor: (theme) => theme.palette["neutral.100"].main,
            boxShadow: (theme) =>
              `-.5px -.5px 0 .5px ${theme.palette["neutral.200"].main}, 1px 1px 2px ${theme.palette["neutral.200"].main}`,
            color: (theme) => theme.palette["neutral.700"].main,

            my: 1,
          },
        },
      }}
    />
  )
}

const RemoveAllFacet = ({ onClick }: any) => {
  return (
    <Box flexBasis={{ xs: "100%", md: "unset" }}>
      <ButtonBase
        sx={(theme) => ({
          ...theme.typography["link.xs"],
          "color": theme.palette["neutral.700"].main,
          "background": `linear-gradient(to right, ${theme.palette["neutral.700"].main}, ${theme.palette["neutral.700"].main})`,
          "backgroundSize": "100% 1px",
          "backgroundPosition": "0 100%",
          "backgroundRepeat": "no-repeat",
          "pb": 1.5,
          "ml": { xs: 0, md: 5 },
          "mt": { xs: 3, md: "5px" },
          "transition": "background-size 400ms",
          "opacity": 0.8,
          "flexBasis": "100%",

          "&:hover, &:focus-within": {
            backgroundSize: "0 1px",
            opacity: 1,
          },
        })}
        onClick={onClick}
      >
        Remove All
      </ButtonBase>
    </Box>
  )
}

type SortByProps = {
  inputProps: InputHTMLAttributes<HTMLInputElement>
} & BoxProps<"input">

const SortByField = forwardRef<any, SortByProps>(({ inputProps, ...props }, ref) => (
  <Box ref={ref} position="relative" sx={{ isolation: "isolate" }}>
    <Box
      component="input"
      type="text"
      sx={(theme) => ({
        ...theme.typography["link.xs"],
        "fontSize": 14,
        "bgcolor": theme.palette["neutral.100"].main,
        "border": "none",
        "borderRadius": 1.5,
        "boxShadow": `-.5px -.5px 0 .5px ${theme.palette["neutral.200"].main}, 1px 1px 2px ${theme.palette["neutral.200"].main}`,
        "color": theme.palette["neutral.700"].main,

        "display": "block",
        "fontWeight": 500,
        "height": 48,
        "px": 4,
        "py": 4,
        "width": 110,

        "&::placeholder": {
          color: theme.palette["neutral.700"].main,
          fontWeight: 400,
        },

        "&:focus": {
          "bgcolor": theme.palette["secondary.200"].main,
          "color": theme.palette["secondary.800"].main,
          "outline": "none",

          "&::placeholder": {
            color: theme.palette["secondary.800"].main,
          },

          "& + svg": {
            color: theme.palette["secondary.800"].main,
          },
        },
      })}
      {...props}
      {...inputProps}
    />
    <SvgIcon
      viewBox="0 0 10 6"
      size={10}
      variant="caretDown"
      sx={{
        position: "absolute",
        right: 20,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 1,
        fill: "none",
      }}
    />
  </Box>
))
SortByField.displayName = "SortByField"

type SpecialsCheckboxProps = {
  checked: boolean
  onChange: (value: boolean) => void
}

// A sub component for the specials checkbox
const SpecialsCheckbox = ({ checked, onChange }: SpecialsCheckboxProps) => {
  const onHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked)
  }
  return (
    <Box
      sx={(theme) => ({
        "bgcolor": theme.palette["neutral.100"].main,
        "border": "none",
        "borderRadius": 1.5,
        "boxShadow": `-.5px -.5px 0 .5px ${theme.palette["neutral.200"].main}, 1px 1px 2px ${theme.palette["neutral.200"].main}`,
        "color": theme.palette["neutral.700"].main,

        "display": "flex",
        "justifyContent": "space-between",
        "alignItems": "center",
        "width": 170,
        "fontWeight": 500,
        "height": 48,
        "px": 4,
        "py": 4,

        "&:focus": {
          bgcolor: theme.palette["secondary.200"].main,
          color: theme.palette["secondary.800"].main,
          outline: "none",
        },
      })}
    >
      <Typography
        variant="link.xs"
        sx={{ fontSize: 14, fontStyle: "normal", fontWeight: 400, lineHeight: "190%" }}
      >
        Special Offer
      </Typography>
      <Checkbox
        icon={
          <SvgIcon
            variant="checkboxEmpty"
            size={20}
            sx={{ fill: (theme) => alpha(theme.palette["neutral.800"].main, 0.12) }}
          />
        }
        checkedIcon={
          <SvgIcon
            variant="checkbox"
            size={20}
            sx={{ fill: (theme) => theme.palette["secondary.800"].main }}
          />
        }
        checked={checked}
        onChange={onHandleChange}
        sx={{ color: (theme) => theme.palette["secondary.200"].main, p: 0 }}
        disableRipple
      />
    </Box>
  )
}
